import { BKJListItem } from '@bookjane2/bookjane-design-library';
import { IBKJSelectRenderOptionsProps } from '@bookjane2/bookjane-design-library/lib/components/BKJSelect/BKJSelect.types';
import { FC, Fragment, useCallback, useEffect, useState } from 'react';
import { css } from 'styled-components';
import { UseFormBehaviors_ValidationFnType } from 'hooks/useFormBehaviors/useFormBehaviors.types';
import { IEditJaneViewForm } from 'modals/JaneDetailsModal/views/EditJaneView/EditJaneView.form.context';
import {
  MessageWrapper,
  SeniorityLengthValueInput,
} from 'modals/JaneDetailsModal/views/EditJaneView/EditJaneView.styled';
import i18next from 'i18next';
import { IBKJComboBoxOption } from '@bookjane2/bookjane-design-library/lib/components/BKJComboBox/BKJComboBox.types';
import { Jane, Position, SeniorityMethodEnum, ShiftParticipationEnum } from 'types/graphql';
import { WhiteLabelAccessor } from 'utils/whiteLabelAccessor';
import { DateTime } from 'luxon';
import { ChangeEventType } from '@bookjane2/bookjane-design-library/lib/common.types';
import {
  SelectPositionByDepartmentDataType,
  SelectPositionByDepartmentOptionType,
} from 'modals/JaneDetailsModal/views/EditJaneView/SelectInputPositionByDepartment/SelectInputPositionByDepartment.types';
import { LocationOptionType } from 'components/LocationFilterInput/LocationFilterInput.types';
export interface ComboBoxOption {
  key: string;
  id: string; // Add an 'id' property
  value: string;
  label: string;
}
export const employmentStatuses = (): Omit<
  IBKJComboBoxOption<string, string, string | number>,
  'index'
>[] => [
  {
    id: 'full_time',
    label: i18next.t('groups:FULL_TIME'),
    value: 'Full Time',
    name: 'employmentStatuses',
    key: 'full_time',
  },
  {
    id: 'part_time',
    label: i18next.t('groups:PART_TIME'),
    value: 'Part Time',
    name: 'employmentStatuses',
    key: 'part_time',
  },
  {
    id: 'casual',
    label: i18next.t('groups:CASUAL'),
    value: 'Casual',
    name: 'employmentStatuses',
    key: 'casual',
  },
  {
    id: 'flex',
    label: i18next.t('groups:FLEX'),
    value: 'Flex',
    name: 'employmentStatuses',
    key: 'flex',
  },
];

export const RenderOptions: FC<IBKJSelectRenderOptionsProps> = ({
  name,
  options,
  selected,
  onChange,
}) => {
  return (
    <Fragment>
      {options.length === 0 ? (
        <MessageWrapper>{i18next.t(`team_member:Select month first`)}</MessageWrapper>
      ) : (
        options.map(({ key, label, value }) => (
          <BKJListItem
            name={name}
            selected={selected}
            key={key}
            label={i18next.t(`team_member:${label}`)}
            value={value}
            onChange={onChange}
            css={css`
              background-color: white;
            `}
          />
        ))
      )}
    </Fragment>
  );
};
export const monthToDaysMap: { [key: string]: string[] } = {};

for (let month = 1; month < 13; month++) {
  const monthName = DateTime.fromFormat('2000-01-01', 'yyyy-MM-dd').set({ month }).toFormat('LLLL');
  const daysInMonth = Array.from(
    { length: DateTime.fromFormat('2000-01-01', 'yyyy-MM-dd').set({ month }).daysInMonth || 0 },
    (_, i) => (i + 1).toString(),
  );

  monthToDaysMap[monthName] = daysInMonth;
}

interface ShiftParticipationForCommunity {
  community_id: string;
  shift_participation: ShiftParticipationEnum;
}

export const prepareEditPayload = (
  formValues: IEditJaneViewForm,
  data: Jane,
  isUserAgency: boolean,
  isSameIds: boolean,
  seniorityMethod: SeniorityMethodEnum,
  isMultiLocationTieringEnabled: boolean,
  isMultiLocationSchedulingEnabled: boolean,
  isMultiLocationCallOutEnabled: boolean,
) => {
  const {
    firstName,
    lastName,
    email,
    phone,
    isMobile,
    shiftEmailEnabled,
    shiftSmsEnabled,
    locale,
    location,
    employmentStatus,
    position_id,
    birthDay,
    birthMonth,
    hourlyRate,
    employeeId,
    employeeStartDate,
    selectedSecondaryPositions,
    selectedSecondaryLocationsCallOut,
    selectedSecondaryLocationsScheduling,
  } = formValues;
  const employmentStatusValue = `${employmentStatus}`.replace(' ', '_').toLowerCase();
  const derivedBirthMonth = monthNameToNumber[birthMonth[0]];
  const foundPrimaryPosition = data?.positions.find(
    (position: Position) =>
      position.details.displayId === data?.primaryPosition?.details.displayId &&
      position.type === 'primary',
  );
  const handlePrimaryPosition: ConcatenatedPrimaryArray[] =
    String(data.primaryPosition?.details?.id) !== String(position_id)
      ? [
          {
            id: foundPrimaryPosition?.id,
            position_id: Number(foundPrimaryPosition?.details?.displayId),
            position_type: `${foundPrimaryPosition?.type}`,
            _destroy: 1,
          },
          {
            position_id: Number(position_id[0]),
            position_type: 'primary',
          },
        ]
      : [];
  const secondaryPositionsMapIdsAsNumber: number[] = selectedSecondaryPositions
    .filter((id) => !!id)
    .map(Number);
  const filteredDeleteSecondaryPositions = data.secondaryPositions.filter(
    (position: Position) => !secondaryPositionsMapIdsAsNumber.includes(position.details.displayId),
  );

  const deleteSecondaryPositions: DestroyedPosition[] = filteredDeleteSecondaryPositions.map(
    (position: Position) => ({
      id: position.id,
      position_type: position.type ? position.type : 'additional',
      position_id: Number(position.details?.id) || Number(position.id),
      _destroy: data?.secondaryPositions.some((item: { id: number }) => item.id === position.id)
        ? 1
        : 0,
    }),
  );

  const filteredAddSecondaryPositions = secondaryPositionsMapIdsAsNumber.filter(
    (id) => !data.secondaryPositions.some((position) => position.details.displayId === id),
  );

  const addSecondaryPositions: AdditionalPosition[] = filteredAddSecondaryPositions.map(
    (secondaryPositionId) => ({
      position_type: 'additional',
      position_id: Number(secondaryPositionId),
    }),
  );

  const handleSecondaryPositions: DestroyedAndAddPositionArrays[] = isSameIds
    ? []
    : [...addSecondaryPositions, ...deleteSecondaryPositions];

  const handlePrimaryAndSecondaryPositions: IHandlePrimaryAndSEcondaryPositions[] = [
    ...handleSecondaryPositions,
    ...handlePrimaryPosition,
  ];
  const handlePrimaryAndSecondaryPositionsForAgency: ConcatenatedPrimaryArray[] = [
    {
      position_id: Number(position_id[0]),
      position_type: 'primary',
    },
    ...(selectedSecondaryPositions?.length
      ? selectedSecondaryPositions.map((secondaryPositionId) => ({
          position_id: Number(secondaryPositionId),
          position_type: 'additional',
        }))
      : []),
  ];

  const rankingSeniorityRankingAttributes =
    seniorityMethod === SeniorityMethodEnum.years_months_days
      ? {
          seniority_years: Number(formValues?.seniorityYears),
          seniority_months: Number(formValues?.seniorityMonths),
          seniority_days: Number(formValues?.seniorityDays),
        }
      : {
          seniority_hours: Number(formValues?.seniorityHours),
        };

  // If MULTI_LOCATION_TIERING is enabled and seniority ranking have a value greater than 0, then pass them
  const seniorityLength =
    isMultiLocationTieringEnabled &&
    Object.values(rankingSeniorityRankingAttributes).some((value) => value && value > 0)
      ? { ranking_seniority_date_attributes: rankingSeniorityRankingAttributes }
      : {};

  const users_communities_attributes: UsersCommunitiesAttributes[] = [];

  if (
    isMultiLocationSchedulingEnabled ||
    isMultiLocationTieringEnabled ||
    isMultiLocationCallOutEnabled
  ) {
    const originalCommunities = data?.usersCommunities || [];

    const updatedCommunities: ShiftParticipationForCommunity[] = [
      ...(isMultiLocationSchedulingEnabled
        ? selectedSecondaryLocationsScheduling.map((communityId) => ({
            community_id: communityId,
            shift_participation: ShiftParticipationEnum.scheduling,
          }))
        : []),
      ...(isMultiLocationTieringEnabled || isMultiLocationCallOutEnabled
        ? selectedSecondaryLocationsCallOut.map((communityId) => ({
            community_id: communityId,
            shift_participation: ShiftParticipationEnum.callout,
          }))
        : []),
    ].reduce((acc: ShiftParticipationForCommunity[], curr) => {
      const existing = acc.find((item) => item.community_id === curr.community_id);

      if (existing) {
        existing.shift_participation = ShiftParticipationEnum.all;
      } else {
        acc.push(curr);
      }

      return acc;
    }, []);

    const addedCommunities = updatedCommunities.filter(
      (updatedCommunity) =>
        !originalCommunities.some((originalCommunity) => {
          const isSameCommunity =
            originalCommunity.community.displayId === updatedCommunity.community_id;
          const isSameParticipation =
            originalCommunity.shiftParticipation === updatedCommunity.shift_participation;
          return isSameCommunity && isSameParticipation;
        }),
    );

    const deletedCommunities = originalCommunities.filter(
      (originalCommunity) =>
        !updatedCommunities.some((updatedCommunity) => {
          const isSameCommunity =
            originalCommunity.community.displayId === updatedCommunity.community_id;
          const isSameParticipation =
            originalCommunity.shiftParticipation === updatedCommunity.shift_participation;
          return isSameCommunity && isSameParticipation;
        }),
    );

    addedCommunities.forEach((community) => {
      users_communities_attributes.push({
        community_id: Number(community.community_id),
        shift_participation: community.shift_participation,
      });
    });

    deletedCommunities.forEach((community) => {
      users_communities_attributes.push({
        id: community.displayId,
        community_id: Number(community.community.displayId),
        shift_participation: community.shiftParticipation,
        _destroy: true,
      });
    });
  }

  return {
    jane: {
      first_name: firstName,
      last_name: lastName,
      email: email,
      phone: phone,
      shift_sms_enabled: shiftSmsEnabled,
      shift_email_enabled: shiftEmailEnabled,
      ...(WhiteLabelAccessor.isPetalProvider ? { locale: locale } : { locale: 'en' }),
      ...(isUserAgency ? {} : { is_mobile: isMobile }),
      ...(isUserAgency ? {} : { employment_status: employmentStatusValue }),
      ...(isUserAgency
        ? { agency_community_id: Number(location?.id) }
        : { primary_community_id: location?.id }),
      birth_day: birthDay[0],
      birth_month: derivedBirthMonth,
      ...(isUserAgency ? {} : { hourly_rate: hourlyRate }),
      employee_number: employeeId,
      employee_start_date: `${employeeStartDate}`,
      users_communities_attributes,
      employee_positions_attributes: isUserAgency
        ? handlePrimaryAndSecondaryPositionsForAgency
        : handlePrimaryAndSecondaryPositions,
      ...(isUserAgency ? {} : { ...seniorityLength }),
    },
  };
};

export const VALIDATION_required = (value: string) => {
  if (value.length === 0 || !value[0]) {
    return i18next.t(`team_member:MANDATORY_FIELD`);
  }
  return '';
};
export const VALIDATION_position_id: UseFormBehaviors_ValidationFnType<IEditJaneViewForm> = (
  value,
): string => {
  const position_id = value as string[];
  if (position_id.length === 0) return i18next.t(`team_member:MUST_SELECT_AT_LEAST_ONE_POSITION`);
  return '';
};

export const monthNameToNumber: { [key: string]: number } = {
  January: 1,
  February: 2,
  March: 3,
  April: 4,
  May: 5,
  June: 6,
  July: 7,
  August: 8,
  September: 9,
  October: 10,
  November: 11,
  December: 12,
};
export const monthNumberToName: { [key: number]: string } = {
  1: 'January',
  2: 'February',
  3: 'March',
  4: 'April',
  5: 'May',
  6: 'June',
  7: 'July',
  8: 'August',
  9: 'September',
  10: 'October',
  11: 'November',
  12: 'December',
};

export const mergeOptions = (
  currentOptions: LocationOptionType[],
  newOptions: LocationOptionType[],
) => {
  const merged = new Map(currentOptions.map((option) => [option.id, option]));

  newOptions.forEach((option) => {
    merged.set(option.id, option);
  });

  return Array.from(merged.values());
};

export interface AdditionalPosition {
  position_type: string;
  position_id: number;
}

export interface DestroyedPosition {
  id?: number;
  position_type?: string;
  position_id?: number;
  _destroy?: number;
}
interface PrimaryPositionWithId {
  id: number;
  position_id: number;
  position_type: string;
  _destroy: number;
}

interface PrimaryPositionWithoutId {
  position_id: number;
  position_type: string;
}

export type ConcatenatedPrimaryArray = PrimaryPositionWithId | PrimaryPositionWithoutId;

export type DestroyedAndAddPositionArrays = DestroyedPosition | AdditionalPosition;
export type IHandlePrimaryAndSEcondaryPositions =
  | DestroyedAndAddPositionArrays
  | ConcatenatedPrimaryArray;

export type UsersCommunitiesAttributes = {
  id?: number;
  community_id: number;
  shift_participation: ShiftParticipationEnum;
  _destroy?: boolean;
};

export interface DropdownItem {
  id: string;
  value: string;
  label: string;
  key: string;
}

export const SeniorityLengthInput: FC<{
  name: string;
  value: string;
  label: string;
  placeholder: string;
  upperLimit?: number;
  onChange: ChangeEventType;
}> = ({ name, value = '', label = '', placeholder, upperLimit = Number.MAX_VALUE, onChange }) => {
  const [displayValue, setDisplayValue] = useState(value ? `${value} ${label}` : '');
  useEffect(() => {
    setDisplayValue(value && !!Number(value) ? `${value} ${label}` : '');
  }, [value, label]);
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      let numericValue = e.target.value.replace(/\D/g, '');
      if (numericValue && !Number.isNaN(numericValue)) {
        if (Number(numericValue) > upperLimit) {
          numericValue = `${upperLimit}`;
        }
      }
      setDisplayValue(numericValue ? `${numericValue} ${label}` : '');
      e.target.value = numericValue;
      e.target.setSelectionRange(0, 0);
      onChange(e);

      // Set cursor position to last digit of numeric value
      setTimeout(() => {
        e.target.setSelectionRange(`${numericValue}`.length, `${numericValue}`.length);
      }, 0);
    },
    [label, onChange, upperLimit],
  );
  return (
    <Fragment>
      <SeniorityLengthValueInput
        name={name}
        onChange={handleChange}
        value={displayValue}
        placeholder={placeholder}
      />
    </Fragment>
  );
};

export type GroupedPositionByDepartmentSelectOptionType = SelectPositionByDepartmentOptionType & {
  groupId: string;
  isGroupNode?: boolean;
};

export const getPositionByDepartmentSelectOptions = (data: SelectPositionByDepartmentDataType) => {
  return Object.entries(data).reduce(
    (
      acc: GroupedPositionByDepartmentSelectOptionType[],
      [department, positions]: [string, SelectPositionByDepartmentOptionType[]],
    ) => {
      acc.push({
        key: department,
        value: department,
        label: department,
        groupId: department,
        isGroupNode: true,
      });
      acc.push(
        ...positions.map(({ value, label, key }) => ({
          key: key,
          value: value,
          label: label,
          groupId: department,
        })),
      );
      return acc;
    },
    [],
  );
};

export const mapPositionIdsByDepartment = (data: SelectPositionByDepartmentDataType) => {
  const result: { [key: string]: string[] } = {};

  for (const [department, positions] of Object.entries(data)) {
    result[department] = positions.map(({ value }) => value);
  }

  return result;
};
